import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Set initial user ID from environment
let userId = process.env.REACT_APP_DEFAULT_USER_ID;
const authToken = localStorage.getItem('authToken');

if (authToken) {
  userId = authToken;
}

export const setUserId = (newUserId) => {
  userId = newUserId;
};

// Request interceptor to add user ID to headers
apiClient.interceptors.request.use((config) => {
  if (userId) {
    config.headers['user-Id'] = userId;
  }
  
  config.headers['Content-Type'] = 'application/json';
  config.headers['Accept'] = 'application/json';

  return config;
});

apiClient.requestEndpoint = async (method, endpoint, data) => {
  const url = endpoint.startsWith('/api/') ? endpoint : `/api/${endpoint}/v1`;
  return apiClient({ method, url, data });
};

// Authentication
export const login = async (credentials) => {
  try {
    const response = await apiClient.post('/api/auth/login/v1', credentials);
    const userData = response.data;

    setUserId(userData.data.token);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCurrentUser = async () => {
  // The interceptor will automatically add User-Id header
  const response = await apiClient.get('/api/auth/me/v1');
  return response.data;
};

// Users API
export const fetchUsers = async () => {
  return apiClient.get('/api/users/v1');
};

export const createUser = async (userData) => {
  try {
    const response = await apiClient.post('/api/users/v1', userData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const updateUser = async (id, userData) => {
  return apiClient.put(`/api/users/v1/${id}`, userData);
};

export const deleteUser = async (id) => {
  return apiClient.delete(`/api/users/v1/${id}`);
};

// Addresses API
export const fetchAddresses = async () => {
  return apiClient.get('/api/addresses/v1');
};

export const createAddress = async (addressData) => {
  return apiClient.post('/api/addresses/v1', addressData);
};

export const updateAddress = async (id, addressData) => {
  return apiClient.put(`/api/addresses/v1/${id}`, addressData);
};

export const deleteAddress = async (id) => {
  return apiClient.delete(`/api/addresses/v1/${id}`);
};

// Alerts API
export const fetchAlerts = async () => {
  return apiClient.get('/api/alerts/v1');
};

export const createAlert = async (alertData) => {
  return apiClient.post('/api/alerts/v1', alertData);
};

export const updateAlert = async (id, alertData) => {
  return apiClient.put(`/api/alerts/v1/${id}`, alertData);
};

export const deleteAlert = async (id) => {
  return apiClient.delete(`/api/alerts/v1/${id}`);
};

// Articles API
export const fetchArticles = async () => {
  return apiClient.get('/api/articles/v1');
};

export const getArticleById = async (id) => {
  try {
    const response = await apiClient.get(`/api/articles/v1/${id}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || 'Failed to fetch article');
  }
};

export const createArticleClip = async (articleData) => {
  return apiClient.post('/api/articles/v1', articleData);
};

export const updateArticle = async (id, articleData) => {
  return apiClient.put(`/api/articles/v1/${id}`, articleData);
};

export const deleteArticle = async (id) => {
  return apiClient.delete(`/api/articles/v1/${id}`);
};

// Companies API
export const fetchCompanies = async () => {
  return apiClient.get('/api/companies/v1');
};

export const createCompany = async (companyData) => {
  return apiClient.post('/api/companies/v1', companyData);
};

export const updateCompany = async (id, companyData) => {
  return apiClient.put(`/api/companies/v1/${id}`, companyData);
};

export const deleteCompany = async (id) => {
  return apiClient.delete(`/api/companies/v1/${id}`);
};

// Company Addresses API
export const fetchCompanyAddresses = async () => {
  return apiClient.get('/api/company_addresses/v1');
};

export const createCompanyAddress = async (companyAddressData) => {
  return apiClient.post('/api/company_addresses/v1', companyAddressData);
};

export const updateCompanyAddress = async (id, companyAddressData) => {
  return apiClient.put(`/api/company_addresses/v1/${id}`, companyAddressData);
};

export const deleteCompanyAddress = async (id) => {
  return apiClient.delete(`/api/company_addresses/v1/${id}`);
};

// Configurations API
export const fetchConfigurations = async () => {
  return apiClient.get('/api/configurations/v1');
};

export const createConfiguration = async (configurationData) => {
  return apiClient.post('/api/configurations/v1', configurationData);
};

export const updateConfiguration = async (id, configurationData) => {
  return apiClient.put(`/api/configurations/v1/${id}`, configurationData);
};

export const deleteConfiguration = async (id) => {
  return apiClient.delete(`/api/configurations/v1/${id}`);
};

// DailyKeywordFacebook API
export const fetchDailyKeywordFacebook = async () => {
  return apiClient.get('/api/daily_keyword_facebook/v1');
};

export const createDailyKeywordFacebook = async (dailyKeywordFacebookData) => {
  return apiClient.post('/api/daily_keyword_facebook/v1', dailyKeywordFacebookData);
};

export const updateDailyKeywordFacebook = async (id, dailyKeywordFacebookData) => {
  return apiClient.put(`/api/daily_keyword_facebook/v1/${id}`, dailyKeywordFacebookData);
};

export const deleteDailyKeywordFacebook = async (id) => {
  return apiClient.delete(`/api/daily_keyword_facebook/v1/${id}`);
};

// DailyKeywordInstagram API
export const fetchDailyKeywordInstagram = async () => {
  return apiClient.get('/api/daily_keyword_instagram/v1');
};

export const createDailyKeywordInstagram = async (dailyKeywordInstagramData) => {
  return apiClient.post('/api/daily_keyword_instagram/v1', dailyKeywordInstagramData);
};

export const updateDailyKeywordInstagram = async (id, dailyKeywordInstagramData) => {
  return apiClient.put(`/api/daily_keyword_instagram/v1/${id}`, dailyKeywordInstagramData);
};

export const deleteDailyKeywordInstagram = async (id) => {
  return apiClient.delete(`/api/daily_keyword_instagram/v1/${id}`);
};

// DailyKeywordThread API
export const fetchDailyKeywordThread = async () => {
  return apiClient.get('/api/daily_keyword_threads/v1');
};

export const createDailyKeywordThread = async (dailyKeywordThreadData) => {
  return apiClient.post('/api/daily_keyword_threads/v1', dailyKeywordThreadData);
};

export const updateDailyKeywordThread = async (id, dailyKeywordThreadData) => {
  return apiClient.put(`/api/daily_keyword_threads/v1/${id}`, dailyKeywordThreadData);
};

export const deleteDailyKeywordThread = async (id) => {
  return apiClient.delete(`/api/daily_keyword_threads/v1/${id}`);
};

// DailyKeywordTikTok API
export const fetchDailyKeywordTikTok = async () => {
  return apiClient.get('/api/daily_keyword_tiktoks/v1');
};

export const createDailyKeywordTikTok = async (dailyKeywordTikTokData) => {
  return apiClient.post('/api/daily_keyword_tiktoks/v1', dailyKeywordTikTokData);
};

export const updateDailyKeywordTikTok = async (id, dailyKeywordTikTokData) => {
  return apiClient.put(`/api/daily_keyword_tiktoks/v1/${id}`, dailyKeywordTikTokData);
};

export const deleteDailyKeywordTikTok = async (id) => {
  return apiClient.delete(`/api/daily_keyword_tiktoks/v1/${id}`);
};

// DailyKeywordTruth API
export const fetchDailyKeywordTruth = async () => {
  return apiClient.get('/api/daily_keyword_truths/v1');
};

export const createDailyKeywordTruth = async (dailyKeywordTruthData) => {
  return apiClient.post('/api/daily_keyword_truths/v1', dailyKeywordTruthData);
};

export const updateDailyKeywordTruth = async (id, dailyKeywordTruthData) => {
  return apiClient.put(`/api/daily_keyword_truths/v1/${id}`, dailyKeywordTruthData);
};

export const deleteDailyKeywordTruth = async (id) => {
  return apiClient.delete(`/api/daily_keyword_truths/v1/${id}`);
};

// DailyKeywordTweet API
export const fetchDailyKeywordTweet = async () => {
  return apiClient.get('/api/daily_keyword_tweets/v1');
};

export const createDailyKeywordTweet = async (dailyKeywordTweetData) => {
  return apiClient.post('/api/daily_keyword_tweets/v1', dailyKeywordTweetData);
};

export const updateDailyKeywordTweet = async (id, dailyKeywordTweetData) => {
  return apiClient.put(`/api/daily_keyword_tweets/v1/${id}`, dailyKeywordTweetData);
};

export const deleteDailyKeywordTweet = async (id) => {
  return apiClient.delete(`/api/daily_keyword_tweets/v1/${id}`);
};

// Instagram Posts API
export const fetchInstagramPost = async () => {
  return apiClient.get('/api/instaram_posts/v1');
};

export const createInstagramPost = async (instagramPostData) => {
  return apiClient.post('/api/instaram_posts/v1', instagramPostData);
};

export const updateInstagramPost = async (id, instagramPostData) => {
  return apiClient.put(`/api/instaram_posts/v1/${id}`, instagramPostData);
};

export const deleteInstagramPost = async (id) => {
  return apiClient.delete(`/api/instaram_posts/v1/${id}`);
};

// Keywords API
export const fetchKeywords = async () => {
  return apiClient.get('/api/keywords/v1');
};

export const createKeyword = async (keywordData) => {
  return apiClient.post('/api/keywords/v1', keywordData);
};

export const updateKeyword = async (id, keywordData) => {
  return apiClient.put(`/api/keywords/v1/${id}`, keywordData);
};

export const deleteKeyword = async (id) => {
  return apiClient.delete(`/api/keywords/v1/${id}`);
};

// Permissions API
export const fetchPermissions = async () => {
  return apiClient.get('/api/permissions/v1');
};

export const createPermission = async (permissionData) => {
  return apiClient.post('/api/permissions/v1', permissionData);
};

export const updatePermission = async (id, permissionData) => {
  return apiClient.put(`/api/permissions/v1/${id}`, permissionData);
};

export const deletePermission = async (id) => {
  return apiClient.delete(`/api/permissions/v1/${id}`);
};

// Role Permissions API
export const fetchRolePermission = async () => {
  return apiClient.get('/api/role_permissions/v1');
};

export const createRolePermission = async (rolePermissionData) => {
  return apiClient.post('/api/role_permissions/v1', rolePermissionData);
};

export const updateRolePermission = async (id, rolePermissionData) => {
  return apiClient.put(`/api/role_permissions/v1/${id}`, rolePermissionData);
};

export const deleteRolePermission = async (id) => {
  return apiClient.delete(`/api/role_permissions/v1/${id}`);
};

// Roles API
export const fetchRoles = async () => {
  return apiClient.get('/api/roles/v1');
};

export const createRole = async (roleData) => {
  return apiClient.post('/api/roles/v1', roleData);
};

export const updateRole = async (id, roleData) => {
  return apiClient.put(`/api/roles/v1/${id}`, roleData);
};

export const deleteRole = async (id) => {
  return apiClient.delete(`/api/roles/v1/${id}`);
};

// TikTokChallenges API
export const fetchTikTokChallenge = async () => {
  return apiClient.get('/api/tiktok_challenges/v1');
};

export const createTikTokChallenge = async (tikTokChallengeData) => {
  return apiClient.post('/api/tiktok_challenges/v1', tikTokChallengeData);
};

export const updateTikTokChallenge = async (id, tikTokChallengeData) => {
  return apiClient.put(`/api/tiktok_challenges/v1/${id}`, tikTokChallengeData);
};

export const deleteTikTokChallenge = async (id) => {
  return apiClient.delete(`/api/tiktok_challenges/v1/${id}`);
};

// TruthTags API
export const fetchTruthTag = async () => {
  return apiClient.get('/api/tiktok_challenges/v1');
};

export const createTruthTag = async (truthTagData) => {
  return apiClient.post('/api/truth_tags/v1', truthTagData);
};

export const updateTruthTag = async (id, truthTagData) => {
  return apiClient.put(`/api/truth_tags/v1/${id}`, truthTagData);
};

export const deleteTruthTag = async (id) => {
  return apiClient.delete(`/api/truth_tags/v1/${id}`);
};

// User Addresses API
export const fetchUserAddress = async () => {
  return apiClient.get('/api/user_addresses/v1');
};

export const createUserAddress = async (userAddressData) => {
  return apiClient.post('/api/user_addresses/v1', userAddressData);
};

export const updateUserAddress = async (id, userAddressData) => {
  return apiClient.put(`/api/user_addresses/v1/${id}`, userAddressData);
};

export const deleteUserAddress = async (id) => {
  return apiClient.delete(`/api/user_addresses/v1/${id}`);
};

// User Alert API
export const fetchUserAlert = async () => {
  return apiClient.get('/api/user_alerts/v1');
};

export const createUserAlert = async (userAlertData) => {
  return apiClient.post('/api/user_alerts/v1', userAlertData);
};

export const updateUserAlert = async (id, userAlertData) => {
  return apiClient.put(`/api/user_alerts/v1/${id}`, userAlertData);
};

export const deleteUserAlert = async (id) => {
  return apiClient.delete(`/api/user_alerts/v1/${id}`);
};

// User Role API
export const fetchUserRole = async () => {
  return apiClient.get('/api/user_roles/v1');
};

export const createUserRole = async (userRoleData) => {
  return apiClient.post('/api/user_roles/v1', userRoleData);
};

export const updateUserRole = async (id, userRoleData) => {
  return apiClient.put(`/api/user_roles/v1/${id}`, userRoleData);
};

export const deleteUserRole = async (id) => {
  return apiClient.delete(`/api/user_roles/v1/${id}`);
};

// XUsers API
export const fetchXUser = async () => {
  return apiClient.get('/api/x_users/v1');
};

export const createXUser = async (xUserData) => {
  return apiClient.post('/api/x_users/v1', xUserData);
};

export const updateXUser = async (id, xUserData) => {
  return apiClient.put(`/api/x_users/v1/${id}`, xUserData);
};

export const deleteXUser = async (id) => {
  return apiClient.delete(`/api/x_users/v1/${id}`);
};
export default apiClient;
